import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, Database, UserCheck, FileText } from 'lucide-react';
export function PrivacyPolicyPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-1607" data-mp="1xqd5bhgodrch">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1608" data-mp="1xsh63qja2a8j">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-1609" data-mp="22ranvv5lknei">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-1610" data-mp="1eybwpne3mmpv">
            <span className="w-8 h-px bg-teal" data-id="element-1611" data-mp="1j1gw3h2vu8aa"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-1612" data-mp="1iyvv0uaerwdl">
              Legal
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-1613" data-mp="2ufui0ge2sdno"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-1614" data-mp="2ui8durgkxtwa">
            Privacy Policy
          </h1>

          <p className="text-lg text-ink-muted" data-id="element-1615" data-mp="8qwi2okozn97">
            Last Updated:{' '}
            {new Date().toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          })}
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1616" data-mp="3qwiyh75szpeo">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="prose prose-lg max-w-none" data-id="element-1617" data-mp="3m3gdhnf3doa3">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-1618" data-mp="335n9siwn9abn">
            At SyNeural, we are committed to protecting your privacy and
            handling your personal information with care and transparency. This
            Privacy Policy explains how we collect, use, store, and protect your
            information when you use our systems, website, and services.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1619" data-mp="2jm6hw09hp3qe">
            By using SyNeural's services, you agree to the practices described
            in this Privacy Policy. If you do not agree with our policies and
            practices, please do not use our services.
          </p>
        </motion.div>
      </section>

      {/* Content Sections */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto space-y-16" data-id="element-1620" data-mp="32hwltt0zga6x">
        {/* Information We Collect */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1621" data-mp="37fptyrluje9u">
          <div className="flex items-center gap-4 mb-6" data-id="element-1622" data-mp="1ogrye97wukl1">
            <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-1623" data-mp="1jj8kxbj1ebu5">
              <Database size={24} data-id="element-1624" data-mp="1xa5122vs10kb" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1625" data-mp="1ta36sesdf9f2">
              Information We Collect
            </h2>
          </div>

          <div className="space-y-6" data-id="element-1626" data-mp="15l1welzz9o75">
            <div data-id="element-1627" data-mp="15m3ehafevayd">
              <h3 className="text-xl font-heading font-bold text-ink-dark mb-3" data-id="element-1628" data-mp="10lfieacbs3xa">
                Personal Information
              </h3>
              <p className="text-ink-muted leading-relaxed mb-3" data-id="element-1629" data-mp="1elq7b7u2r8ft">
                We may collect personal information that you provide directly to
                us, including:
              </p>
              <ul className="list-disc list-inside space-y-2 text-ink-muted ml-4" data-id="element-1630" data-mp="2pysmpzkftrye">
                <li data-id="element-1631" data-mp="2l1skjzqpl9ck">Name, email address, and contact information</li>
                <li data-id="element-1632" data-mp="3gb79f0mazjrv">Organization or institutional affiliation</li>
                <li data-id="element-1633" data-mp="3gd0dq99g1rwp">Account credentials and authentication information</li>
                <li data-id="element-1634" data-mp="3lb1w0rz1pn74">Profile information and preferences</li>
                <li data-id="element-1635" data-mp="3q9myky1rsiyq">Communication history and support inquiries</li>
              </ul>
            </div>

            <div data-id="element-1636" data-mp="3v86ikswszkog">
              <h3 className="text-xl font-heading font-bold text-ink-dark mb-3" data-id="element-1637" data-mp="3v8q1ocy6wf42">
                Assessment and Platform Data
              </h3>
              <p className="text-ink-muted leading-relaxed mb-3" data-id="element-1638" data-mp="32kv6hux8088">
                When you use our CJI and NCJI assessment tools, we collect:
              </p>
              <ul className="list-disc list-inside space-y-2 text-ink-muted ml-4" data-id="element-1639" data-mp="cvi9ebsvr311">
                <li data-id="element-1640" data-mp="htthagzm73gm">Assessment responses and performance data</li>
                <li data-id="element-1641" data-mp="hvebu6lximeh">Reasoning patterns and decision-making metrics</li>
                <li data-id="element-1642" data-mp="mu7ojwte69dl">Development pathway selections and progress tracking</li>
                <li data-id="element-1643" data-mp="qv9rooqfj5ku">
                  Aggregated and anonymized capability data for research
                  purposes
                </li>
              </ul>
            </div>

            <div data-id="element-1644" data-mp="27a5tnnc9297i">
              <h3 className="text-xl font-heading font-bold text-ink-dark mb-3" data-id="element-1645" data-mp="279m8zyv7t6ps">
                Technical Information
              </h3>
              <p className="text-ink-muted leading-relaxed mb-3" data-id="element-1646" data-mp="22dovs1f90gh8">
                We automatically collect certain technical information,
                including:
              </p>
              <ul className="list-disc list-inside space-y-2 text-ink-muted ml-4" data-id="element-1647" data-mp="2uzqb21sr8yeo">
                <li data-id="element-1648" data-mp="2q2hsw17lwudf">Device information, browser type, and operating system</li>
                <li data-id="element-1649" data-mp="2l9oo5dc51m1c">IP address and general location data</li>
                <li data-id="element-1650" data-mp="2l83vjt9dwd64">Usage data, page views, and interaction patterns</li>
                <li data-id="element-1651" data-mp="2gb3obf0st4df">Cookies and similar tracking technologies</li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* How We Use Your Information */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1652" data-mp="38z9am67i31fd">
          <div className="flex items-center gap-4 mb-6" data-id="element-1653" data-mp="xc623n8r5n1a">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-1654" data-mp="12b8wmdr3wav0">
              <Eye size={24} data-id="element-1655" data-mp="ndzzt4j1aevw" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1656" data-mp="nd6so2g5xnd8">
              How We Use Your Information
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-1657" data-mp="eed32w191x96">
            We use the information we collect for the following purposes:
          </p>
          <ul className="list-disc list-inside space-y-3 text-ink-muted ml-4" data-id="element-1658" data-mp="jd6cac9i723j">
            <li data-id="element-1659" data-mp="28sdxn4prny3g">To provide, maintain, and improve our systems and services</li>
            <li data-id="element-1660" data-mp="2dpw9b2v64rf5">
              To deliver personalized assessment results, insights, and
              development recommendations
            </li>
            <li data-id="element-1661" data-mp="2nsrpjs7h7ze8">
              To conduct research and analysis to advance our understanding of
              judgment, decision-quality, and human capability
            </li>
            <li data-id="element-1662" data-mp="2wor05mm5aww9">
              To communicate with you about your account, updates, and support
            </li>
            <li data-id="element-1663" data-mp="2rud19a89y7mu">To ensure the security of our systems and prevent fraud or misuse</li>
            <li data-id="element-1664" data-mp="36jsq2ko8w2zw">
              To comply with legal obligations and enforce our terms of service
            </li>
            <li data-id="element-1665" data-mp="31odey0hmgl76">
              To develop new features, tools, and institutional applications
            </li>
          </ul>
        </motion.div>

        {/* Data Sharing and Disclosure */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1666" data-mp="3bl8acailm5h8">
          <div className="flex items-center gap-4 mb-6" data-id="element-1667" data-mp="25p9m2chsvfwb">
            <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-1668" data-mp="2apf8ne97qc7j">
              <UserCheck size={24} data-id="element-1669" data-mp="2fignjm9f6br4" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1670" data-mp="2jiit46i9zpbo">
              Data Sharing and Disclosure
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-1671" data-mp="1mu2qp9u2hlh5">
            We do not sell your personal information. We may share your
            information in the following circumstances:
          </p>
          <ul className="list-disc list-inside space-y-3 text-ink-muted ml-4" data-id="element-1672" data-mp="1vrmvgsr51zwi">
            <li data-id="element-1673" data-mp="20tc3s0mz7zgw">
              <strong data-id="element-1674" data-mp="20tuaxsb6fy3l">With Your Consent:</strong> When you explicitly authorize
              us to share your information
            </li>
            <li data-id="element-1675" data-mp="3c7fwz4lqpjkw">
              <strong data-id="element-1676" data-mp="3c6wqql5gcjmp">Institutional Partners:</strong> If you access SyNeural
              through an educational institution, employer, or government
              program, we may share relevant data with that organization as
              agreed upon
            </li>
            <li data-id="element-1677" data-mp="3hemh60l8lssf">
              <strong data-id="element-1678" data-mp="3clbhqkir8eaj">Service Providers:</strong> With trusted third-party
              vendors who help us operate our platform (e.g., hosting,
              analytics, customer support)
            </li>
            <li data-id="element-1679" data-mp="33klmbhxeopxc">
              <strong data-id="element-1680" data-mp="33l56l5aj3qej">Research Partners:</strong> Aggregated, anonymized data
              may be shared with research institutions for academic and
              scientific purposes
            </li>
            <li data-id="element-1681" data-mp="2tlxm7y5dmvhx">
              <strong data-id="element-1682" data-mp="2tnh1blfar0i4">Legal Requirements:</strong> When required by law, court
              order, or to protect our rights and safety
            </li>
            <li data-id="element-1683" data-mp="rvcg0ssaz1gd">
              <strong data-id="element-1684" data-mp="rvugy2pwu3um">Business Transfers:</strong> In connection with a merger,
              acquisition, or sale of assets
            </li>
          </ul>
        </motion.div>

        {/* Data Security */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1685" data-mp="2e14zmthyz499">
          <div className="flex items-center gap-4 mb-6" data-id="element-1686" data-mp="2j21355yesoe5">
            <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-1687" data-mp="1vbeubcgkz033">
              <Lock size={24} data-id="element-1688" data-mp="1qclrcdyx6rh6" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1689" data-mp="1qdwviewa218t">
              Data Security
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-1690" data-mp="20ahykuoql2a0">
            We implement industry-standard security measures to protect your
            information, including:
          </p>
          <ul className="list-disc list-inside space-y-3 text-ink-muted ml-4" data-id="element-1691" data-mp="1cikmr3laea8w">
            <li data-id="element-1692" data-mp="17lkhguw9hbiu">Encryption of data in transit and at rest</li>
            <li data-id="element-1693" data-mp="3j8xoxqy187er">Secure authentication and access controls</li>
            <li data-id="element-1694" data-mp="3j7cz2emnxcn8">Regular security audits and vulnerability assessments</li>
            <li data-id="element-1695" data-mp="3o17eh5v8j5gi">Employee training on data protection and privacy practices</li>
            <li data-id="element-1696" data-mp="3a9sryp24cm4p">
              Incident response procedures and breach notification protocols
            </li>
          </ul>
          <p className="text-ink-muted leading-relaxed mt-4" data-id="element-1697" data-mp="3e80anpg57xij">
            While we take reasonable precautions, no method of transmission over
            the internet or electronic storage is 100% secure. We cannot
            guarantee absolute security but are committed to protecting your
            information to the best of our ability.
          </p>
        </motion.div>

        {/* Your Rights and Choices */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1698" data-mp="2hjtzsksmo8vt">
          <div className="flex items-center gap-4 mb-6" data-id="element-1699" data-mp="n3vlw29j1bby">
            <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-1700" data-mp="n4dpxl2fktuf">
              <Shield size={24} data-id="element-1701" data-mp="ib2jhl1j6f31" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1702" data-mp="ebtl22vjpt15">
              Your Rights and Choices
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-1703" data-mp="9dieq24gm0no">
            Depending on your location, you may have the following rights:
          </p>
          <ul className="list-disc list-inside space-y-3 text-ink-muted ml-4" data-id="element-1704" data-mp="3vivuvdx2rt9t">
            <li data-id="element-1705" data-mp="3rhttorjll757">
              <strong data-id="element-1706" data-mp="3ribyickbstoq">Access:</strong> Request a copy of the personal
              information we hold about you
            </li>
            <li data-id="element-1707" data-mp="1tqr0zww0mlwg">
              <strong data-id="element-1708" data-mp="1tr97dmce6gi3">Correction:</strong> Request correction of inaccurate or
              incomplete information
            </li>
            <li data-id="element-1709" data-mp="37o3mwln0gdev">
              <strong data-id="element-1710" data-mp="37ollhox5b3py">Deletion:</strong> Request deletion of your personal
              information (subject to legal obligations)
            </li>
            <li data-id="element-1711" data-mp="3clc5uofzglh6">
              <strong data-id="element-1712" data-mp="3cku0917fkuwl">Portability:</strong> Request a copy of your data in a
              structured, machine-readable format
            </li>
            <li data-id="element-1713" data-mp="3lfqjkmkv7ny6">
              <strong data-id="element-1714" data-mp="3le724snh36ut">Opt-Out:</strong> Opt out of certain data processing
              activities, such as marketing communications
            </li>
            <li data-id="element-1715" data-mp="2toayiwyyi9a2">
              <strong data-id="element-1716" data-mp="2tnss57ikyeof">Withdraw Consent:</strong> Withdraw consent for data
              processing where consent is the legal basis
            </li>
          </ul>
          <p className="text-ink-muted leading-relaxed mt-4" data-id="element-1717" data-mp="2xs0g32h7rqll">
            To exercise these rights, please contact us at{' '}
            <a href="mailto:privacy@syneural.com" className="text-teal font-medium hover:underline" data-id="element-1718" data-mp="czx9aph4n8pk">
              privacy@syneural.com
            </a>
            .
          </p>
        </motion.div>

        {/* Data Retention */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1719" data-mp="gjfnm2t2q54a">
          <div className="flex items-center gap-4 mb-6" data-id="element-1720" data-mp="zggvbve0ijbx">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-1721" data-mp="zevz81c1usby">
              <FileText size={24} data-id="element-1722" data-mp="uguezd2w0myw" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1723" data-mp="196ryhfpvgohr">
              Data Retention
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed" data-id="element-1724" data-mp="148i5nbrmdzzb">
            We retain your personal information for as long as necessary to
            provide our services, comply with legal obligations, resolve
            disputes, and enforce our agreements. Assessment data may be
            retained longer for research and system improvement purposes, but
            will be anonymized where possible. You may request deletion of your
            account and associated data at any time, subject to legal retention
            requirements.
          </p>
        </motion.div>

        {/* Children's Privacy */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1725" data-mp="1rsr3oescnvcm">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1726" data-mp="142wss2rlcgv0">
            Children's Privacy
          </h2>
          <p className="text-ink-muted leading-relaxed" data-id="element-1727" data-mp="v4bg2o8svw25">
            SyNeural's services are not directed to children under 13 years of
            age. We do not knowingly collect personal information from children
            under 13. If we become aware that we have collected information from
            a child under 13, we will take steps to delete such information. For
            educational use cases involving minors, we work with schools and
            institutions to ensure appropriate parental consent and data
            protection measures are in place.
          </p>
        </motion.div>

        {/* International Data Transfers */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1728" data-mp="3kqwe5oso4rqn">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1729" data-mp="36rdjnyss5ewt">
            International Data Transfers
          </h2>
          <p className="text-ink-muted leading-relaxed" data-id="element-1730" data-mp="1vcrjb35tfw64">
            Your information may be transferred to and processed in countries
            other than your country of residence. We ensure that such transfers
            comply with applicable data protection laws and implement
            appropriate safeguards, such as standard contractual clauses, to
            protect your information.
          </p>
        </motion.div>

        {/* Changes to This Policy */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1731" data-mp="onyln0wkhsx3">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1732" data-mp="13ha2fxdczdgj">
            Changes to This Privacy Policy
          </h2>
          <p className="text-ink-muted leading-relaxed" data-id="element-1733" data-mp="35b9gurls2axg">
            We may update this Privacy Policy from time to time to reflect
            changes in our practices, technology, legal requirements, or other
            factors. We will notify you of material changes by posting the
            updated policy on our website and updating the "Last Updated" date.
            Your continued use of our services after such changes constitutes
            acceptance of the updated policy.
          </p>
        </motion.div>

        {/* Contact Us */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-surface-muted p-8 rounded-lg border border-line" data-id="element-1734" data-mp="2v8nkd83x8dvm">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1735" data-mp="2bh8syq01bpmo">
            Contact Us
          </h2>
          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-1736" data-mp="3rymuv83mtmgu">
            If you have questions, concerns, or requests regarding this Privacy
            Policy or our data practices, please contact us:
          </p>
          <div className="space-y-2 text-ink-muted" data-id="element-1737" data-mp="3ayjfyl54b6x">
            <p data-id="element-1738" data-mp="3ci401dtx8ei">
              <strong data-id="element-1739" data-mp="38u84vkww9wm">Email:</strong>{' '}
              <a href="mailto:privacy@syneural.com" className="text-teal font-medium hover:underline" data-id="element-1740" data-mp="3lh35u5ipu5xi">
                privacy@syneural.com
              </a>
            </p>
            <p data-id="element-1741" data-mp="i0w5o8zieqth">
              <strong data-id="element-1742" data-mp="i0cy9m7hjktp">General Inquiries:</strong>{' '}
              <a href="mailto:contact@syneural.com" className="text-teal font-medium hover:underline" data-id="element-1743" data-mp="myo3tkqqz964">
                contact@syneural.com
              </a>
            </p>
          </div>
        </motion.div>
      </section>
    </div>;
}